import { useEffect, useRef } from 'react';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';

/**
 * Minimal silent H.264 MP4 (686 bytes) used as an iOS fallback.
 * iOS Safari's Screen Wake Lock API is unreliable — a playing <video>
 * element with `playsinline` reliably prevents screen sleep on all iOS versions.
 */
const SILENT_MP4 =
  'data:video/mp4;base64,AAAAIGZ0eXBpc29tAAACAGlzb21pc28yYXZjMW1wNDEAAAJcbW9vdgAAAGxtdmhkAAAAAAAAAAAAAAAAAAAD6AAAAGQAAQAAAQAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAgAAAeh0cmFrAAAAXHRraGQAAAADAAAAAAAAAAAAAAABAAAAAAAAAGQAAAAAAAAAAAAAAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAABAAAAAAAIAAAACAAAAAAGEbWRpYQAAACBtZGhkAAAAAAAAAAAAAAAAAAAD6AAAAGRVxAAAAAAALWhkbHIAAAAAAAAAAHZpZGUAAAAAAAAAAAAAAABWaWRlb0hhbmRsZXIAAAABL21pbmYAAAAUdm1oZAAAAAEAAAAAAAAAAAAAACRkaW5mAAAAHGRyZWYAAAAAAAAAAQAAAAx1cmwgAAAAAQAAAO9zdGJsAAAAh3N0c2QAAAAAAAAAAQAAAHdhdmMxAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAAAIAAgBIAAAASAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGP//AAAAIWF2Y0MBQsAe/+EACmdCwB7ZAKBH/sgBAARozjiAAAAAGHN0dHMAAAAAAAAAAQAAAAEAAABkAAAAHHN0c2MAAAAAAAAAAQAAAAEAAAABAAAAAQAAABhzdHN6AAAAAAAAAAAAAAABAAAAKgAAABRzdGNvAAAAAAAAAAEAAAKEAAAAMm1kYXQAAAAKZ0LAHtkAoEf+yAAAAARozjiAAAAAEGWIgEAAAAMAAAMAAAMAAAM=';

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/** Which stretch of work the lock is being held for — segments the telemetry. */
export type WakeLockPhase = 'recording' | 'upload' | 'live_session';

/**
 * Which surface acquired the lock — a finer, snake_case label that sub-segments
 * a `phase`. `'upload'` in particular is now emitted by several surfaces (voice
 * transcription, mobile capture, the background upload tray); passing a `source`
 * keeps the wake-lock dashboards separable across them.
 */
export type WakeLockSource = 'upload_manager';

function createNoSleepVideo(): HTMLVideoElement {
  const video = document.createElement('video');
  video.setAttribute('playsinline', '');
  video.setAttribute('loop', '');
  // Decorative: keep the 1px keep-awake shim out of the accessibility tree.
  video.setAttribute('aria-hidden', 'true');
  video.tabIndex = -1;
  video.muted = true;
  video.src = SILENT_MP4;
  video.style.cssText =
    'position:fixed;top:-1px;left:-1px;width:1px;height:1px;opacity:0.01;pointer-events:none;';
  return video;
}

/**
 * Prevents the screen from sleeping while enabled.
 * Uses the Screen Wake Lock API as the primary method and gracefully degrades.
 * On iOS, also plays a silent video as a fallback — the Wake Lock API is
 * unreliable on Safari despite being technically supported since 16.4.
 * Re-acquires the lock when the page regains visibility (OS releases on background).
 */
export function useScreenWakeLock(
  enabled: boolean,
  phase: WakeLockPhase = 'recording',
  source?: WakeLockSource,
): void {
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    // Telemetry tags for this hold. `source` is only included when provided, so
    // callers that don't pass it keep emitting exactly `{ phase }` (no
    // `source: undefined` noise on the event).
    const eventProps = source ? { phase, source } : { phase };

    // `enabled` toggles within a single capture (recording -> review -> upload),
    // so the effect re-runs and every async step below must check whether it was
    // torn down mid-flight. Without this an in-flight request resolves after
    // cleanup and installs a sentinel nothing will ever release.
    let disposed = false;
    // Dedupe overlapping acquires: init() and the visibilitychange handler can
    // both fire a request, and without this the second orphans the first's
    // sentinel (whose own release event can no longer clear the ref).
    let requesting = false;

    if (!enabled) {
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.remove();
        videoRef.current = null;
      }
      return;
    }

    const acquireWakeLock = async (): Promise<boolean> => {
      if (!('wakeLock' in navigator)) return false;
      if (requesting || wakeLockRef.current) return false;
      requesting = true;
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (disposed) {
          // Torn down while the request was in flight — release immediately
          // rather than leaking a lock that holds the screen on forever.
          void sentinel.release().catch(() => {});
          return false;
        }
        wakeLockRef.current = sentinel;
        safeAnalytics.track(ANALYTICS_EVENTS.SCREEN_WAKE_LOCK_ACQUIRED, eventProps);
        sentinel.addEventListener('release', () => {
          // Identity check: the release event fires asynchronously, so a stale
          // sentinel's event must not null out a newer, live one.
          if (wakeLockRef.current === sentinel) wakeLockRef.current = null;
        });
        return true;
      } catch {
        if (!disposed) safeAnalytics.track(ANALYTICS_EVENTS.SCREEN_WAKE_LOCK_FAILED, eventProps);
        return false;
      } finally {
        requesting = false;
      }
    };

    const startVideoFallback = () => {
      if (disposed || videoRef.current) return;
      const video = createNoSleepVideo();
      document.body.appendChild(video);
      videoRef.current = video;
      // Track the OUTCOME, not the attempt. On iOS the video IS the keep-awake
      // mechanism, and play() can be rejected (gesture gating); a swallowed
      // rejection would leave the screen free to sleep while we report success.
      video
        .play()
        ?.then(() => {
          safeAnalytics.track(ANALYTICS_EVENTS.SCREEN_WAKE_LOCK_VIDEO_FALLBACK, eventProps);
        })
        .catch(() => {
          safeAnalytics.track(ANALYTICS_EVENTS.SCREEN_WAKE_LOCK_FAILED, {
            ...eventProps,
            reason: 'video_play_rejected',
          });
        });
    };

    const init = async () => {
      const wakeLockAcquired = await acquireWakeLock();
      if (disposed) return;
      // On iOS: always use video fallback (Wake Lock API is unreliable)
      // On other platforms: use video fallback only when Wake Lock API is unavailable
      if (isIOS() || !wakeLockAcquired) {
        startVideoFallback();
      }
    };

    // iOS gates <video>.play() on a same-task user gesture. Creating the shim
    // after `await acquireWakeLock()` inside init() runs in a LATER task, where
    // play() is rejected and the screen silently sleeps — exactly the failure
    // the upload-phase lock exists to prevent. Start it synchronously here,
    // while the interaction that flipped `enabled` is still the current task;
    // init()'s own startVideoFallback() is then a no-op (videoRef is set).
    if (isIOS()) startVideoFallback();
    void init();

    // Re-acquire on visibility change (OS releases wake lock when backgrounded)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!wakeLockRef.current) {
          void acquireWakeLock();
        }
        if (videoRef.current?.paused) {
          videoRef.current.play()?.catch(() => {});
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.remove();
        videoRef.current = null;
      }
    };
    // `phase` is a per-call-site constant in practice, so including it does not
    // add re-runs — but omitting it would let a stale phase reach analytics.
  }, [enabled, phase, source]);
}
